"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import type { DestinationBridge } from "@/lib/destinations/bridge";

export function CompanionWorkPrompt({
  bridge = null,
  context,
}: {
  bridge?: DestinationBridge | null;
  context: "listing" | "placement";
}) {
  const storageKey = `peakwave:companion-work-prompt:${context}:${bridge?.slug ?? "season"}`;
  const [ready, setReady] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const update = window.setTimeout(() => {
      setDismissed(window.localStorage.getItem(storageKey) === "dismissed");
      setReady(true);
    }, 0);
    return () => window.clearTimeout(update);
  }, [storageKey]);

  if (!ready || dismissed) return null;

  const placeLabel = bridge?.name ?? "your season";
  const title =
    context === "placement"
      ? `Set up Companion for ${placeLabel}`
      : `Bring ${placeLabel} into Companion`;
  const body =
    context === "placement"
      ? "Keep live conditions, plans, people and your season record together once work is sorted."
      : "Follow live conditions and planning context alongside this job, without changing your work profile.";

  return (
    <aside className="relative overflow-hidden rounded-[18px] border border-teal/20 bg-teal/[0.06] px-5 py-5 sm:px-6">
      <button
        type="button"
        aria-label="Dismiss Companion suggestion"
        onClick={() => {
          window.localStorage.setItem(storageKey, "dismissed");
          setDismissed(true);
        }}
        className="absolute right-2 top-2 flex min-h-11 min-w-11 items-center justify-center rounded-full text-xl text-text-muted transition hover:bg-white hover:text-navy"
      >
        ×
      </button>
      <div className="pr-9">
        <p className="text-[10px] font-semibold uppercase tracking-[2px] text-teal">
          Companion
        </p>
        <h2 className="mt-1 font-serif text-[24px] text-navy">{title}</h2>
        <p className="mt-2 max-w-[650px] text-[13px] leading-6 text-text-muted">
          {body}
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <Link
            href="/companion"
            className="inline-flex min-h-11 items-center justify-center rounded-sm bg-navy px-4 py-2.5 text-sm font-semibold text-white no-underline transition hover:bg-gold"
          >
            Set up Companion
          </Link>
          {bridge?.conditionRoute ? (
            <Link
              href={bridge.conditionRoute}
              className="inline-flex min-h-11 items-center justify-center rounded-sm border border-navy/15 bg-white px-4 py-2.5 text-sm font-semibold text-navy no-underline transition hover:border-gold hover:text-gold"
            >
              View live conditions
            </Link>
          ) : null}
        </div>
      </div>
    </aside>
  );
}
